import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useWatch } from 'react-hook-form';

import {
  ListItem,
  ListItemContainer
} from 'reservations/base/components/ListItem';
import { ModalType, useModal } from 'reservations/base/providers/ModalProvider';
import {
  useBookingGuests,
  useNumberOfGuests
} from 'reservations/booking/providers/BookingProvider';
import {
  ChecklessBadge,
  HostBadge
} from 'reservations/guests/components/GuestStatusBadges';
import { useIsValidPartySize } from 'reservations/guests/hooks/useIsValidPartySize';

export const GuestList = ({
  allowRemoval = true,
  includeDividers = true,
  validatePartySize = false,
  showHeader = true
}) => {
  const guests = useBookingGuests();
  const { open } = useModal();
  const numberOfGuests = useNumberOfGuests();
  const partySize = useWatch({ name: 'partySize' });
  const header = `GUESTS (${numberOfGuests}/${partySize})`;

  const validPartySize = useIsValidPartySize();
  const headerIcon = validPartySize ? (
    <CheckCircleIcon data-testid="check-circle-icon" />
  ) : (
    <WarningIcon data-testid="warning-icon" />
  );
  const theme = useTheme();

  const GuestContent = guests?.map(
    ({ id, name, lead, opt_in_checkless }, idx) => {
      // Host is diner-facing term, Lead is our internal term
      const Badges = (
        <>
          {lead && <HostBadge />}
          {opt_in_checkless && <ChecklessBadge />}
        </>
      );

      const RemoveButton = (
        <Button
          data-testid="remove-guest-button"
          onClick={() => open(ModalType.RemoveGuest, { visitIdToRemove: id })}
          variant="secondary"
          size="small"
        >
          REMOVE
        </Button>
      );

      return (
        <ListItem
          key={id}
          primaryLabel={name}
          primaryLabelSxOverride={{ fontWeight: 500 }}
          {...(!includeDividers && { pb: '0px' })}
          includeDivider={includeDividers && idx < numberOfGuests - 1}
          inlineContent={Badges}
          endAdornment={allowRemoval && !lead ? RemoveButton : null}
        />
      );
    }
  );

  return (
    <>
      {showHeader && (
        <Typography
          variant="h3"
          sx={{
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            paddingBottom: '16px',
            color:
              validatePartySize && !validPartySize
                ? theme.palette.error.main
                : theme.typography.h3.color,
            '& .MuiSvgIcon-root': {
              fontSize: theme.typography.h3.fontSize,
              marginLeft: '4px'
            }
          }}
          data-testid="guest-list"
        >
          {header}
          {validatePartySize && headerIcon}
        </Typography>
      )}
      <ListItemContainer>{GuestContent}</ListItemContainer>
    </>
  );
};
